import { useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { InvoiceStatusBadge } from "../components/InvoiceStatusBadge";
import {
  useDownloadSupplierInvoicePdf,
  useSupplierInvoiceById,
  useViewSupplierInvoicePdf,
} from "../hooks/useInvoicing";
import { useNotification } from "@/shared/hooks/useNotification";

const money = (value: number) =>
  new Intl.NumberFormat("en-KE", { style: "currency", currency: "KES" }).format(value || 0);

export const SupplierInvoiceDetailPage = () => {
  const { id } = useParams();
  const notification = useNotification();
  const invoiceQuery = useSupplierInvoiceById(id);
  const viewSupplierInvoicePdf = useViewSupplierInvoicePdf();
  const downloadSupplierInvoicePdf = useDownloadSupplierInvoicePdf();

  if (invoiceQuery.isLoading) {
    return <div className="py-10 text-muted-foreground">Loading supplier invoice...</div>;
  }

  if (invoiceQuery.error || !invoiceQuery.data) {
    return <div className="py-10 text-red-600">Failed to load supplier invoice.</div>;
  }

  const invoice = invoiceQuery.data;

  const handleViewInvoice = () => {
    if (!id) return;

    viewSupplierInvoicePdf.mutate(id, {
      onError: () => notification.error("Failed to open supplier invoice PDF"),
    });
  };

  const handleDownloadInvoice = () => {
    if (!id) return;

    downloadSupplierInvoicePdf.mutate(id, {
      onError: () => notification.error("Failed to download supplier invoice PDF"),
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <CardTitle className="flex items-center gap-3">
              Supplier Invoice {invoice.invoiceNumber}
              <InvoiceStatusBadge statusName={invoice.statusName} isOverdue={invoice.isOverdue} />
            </CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={handleViewInvoice} disabled={viewSupplierInvoicePdf.isPending}>
                View Invoice
              </Button>
              <Button variant="outline" onClick={handleViewInvoice} disabled={viewSupplierInvoicePdf.isPending}>
                Print Invoice
              </Button>
              <Button onClick={handleDownloadInvoice} disabled={downloadSupplierInvoicePdf.isPending}>
                Download PDF
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-3 text-sm">
          <div>Supplier: {invoice.supplierName}</div>
          <div>Supplier Invoice #: {invoice.supplierInvoiceNumber}</div>
          <div>PO #: {invoice.purchaseOrderNumber || "-"}</div>
          <div>Invoice Date: {new Date(invoice.invoiceDate).toLocaleDateString()}</div>
          <div>Due Date: {new Date(invoice.dueDate).toLocaleDateString()}</div>
          <div>Payment Method: {invoice.paymentMethod || "-"}</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Financial Summary</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-3 text-sm">
          <div>Subtotal: {money(invoice.subtotal)}</div>
          <div>VAT: {money(invoice.vatAmount)}</div>
          <div>Total: {money(invoice.totalAmount)}</div>
          <div>Paid: {money(invoice.paidAmount)}</div>
          <div>Balance: {money(invoice.balanceAmount)}</div>
          <div>Days Overdue: {invoice.daysOverdue || 0}</div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Line Items</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead>Batch</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Unit Cost</TableHead>
                  <TableHead>VAT</TableHead>
                  <TableHead>Line Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoice.items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="break-words max-w-xs">{item.description}</TableCell>
                    <TableCell>{item.batchNumber || "-"}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell>{money(item.unitCost || 0)}</TableCell>
                    <TableCell>{money(item.vatAmount || 0)}</TableCell>
                    <TableCell>{money(item.lineTotal)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
